import React, { useEffect, useRef, useState } from 'react';
import './SelfHostedSection.css';
import { SELFHOST_INSTALL_COMMAND } from 'utils/installLinks';

// The install script the terminal "types out", line by line, then loops.
// kind: 'cmd' → typed char-by-char behind a green $ prompt
//       'out' → program output, revealed once the command above finishes
// Mirrors the REAL installer: ONE command — it prompts for your model key
// inline (via /dev/tty), loads the images, and compose-ups in the same run.
const SCRIPT = [
  { kind: 'cmd', text: SELFHOST_INSTALL_COMMAND },
  { kind: 'out', text: 'release signature verified · checksums verified', ok: true },
  { kind: 'out', text: 'model key → .env  (Anthropic / OpenAI / Bedrock / local)', ok: true },
  { kind: 'out', text: 'images loaded: control-plane, agent', ok: true },
  { kind: 'out', text: 'control-plane healthy  :3001  · MCP · cron · webhook · Slack', ok: true },
  { kind: 'out', text: 'dashboard: http://localhost:3001  · access token printed above', ok: true },
];

const TYPE_MS = 22;       // per-character typing speed
const CMD_HOLD_MS = 480;  // pause after a command finishes typing
const OUT_HOLD_MS = 260;  // pause after an output line appears
const LOOP_HOLD_MS = 2000; // pause at the end before restarting

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function AnimatedTerminal() {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const [pos, setPos] = useState({ line: 0, char: 0 });
  const timer = useRef(null);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (reduced) return undefined;

    const { line, char } = pos;

    // Finished the whole script → hold, then restart from the top.
    if (line >= SCRIPT.length) {
      timer.current = setTimeout(() => setPos({ line: 0, char: 0 }), LOOP_HOLD_MS);
      return () => clearTimeout(timer.current);
    }

    const current = SCRIPT[line];

    if (current.kind === 'cmd' && char < current.text.length) {
      timer.current = setTimeout(
        () => setPos({ line, char: char + 1 }),
        TYPE_MS,
      );
    } else {
      const hold = current.kind === 'cmd' ? CMD_HOLD_MS : OUT_HOLD_MS;
      timer.current = setTimeout(
        () => setPos({ line: line + 1, char: 0 }),
        hold,
      );
    }
    return () => clearTimeout(timer.current);
  }, [pos, reduced]);

  const visibleCount = reduced ? SCRIPT.length : Math.min(pos.line + 1, SCRIPT.length);

  return (
    <div
      className="selfhost-term"
      role="img"
      aria-label="Terminal: one signed installer (curl … install.sh | bash) verifies the release signature and checksums, prompts inline for your model key, loads the control-plane and agent images, and brings the control plane online on port 3001 with MCP, cron, webhook, and Slack triggers — dashboard at localhost:3001 with your access token printed."
    >
      <div className="selfhost-term-chrome" aria-hidden="true">
        <span className="selfhost-term-dots">
          <span className="selfhost-term-dot selfhost-term-dot--r" />
          <span className="selfhost-term-dot selfhost-term-dot--y" />
          <span className="selfhost-term-dot selfhost-term-dot--g" />
        </span>
        <span className="selfhost-term-title">zibby@vpc — bash</span>
      </div>

      <div className="selfhost-term-body" aria-hidden="true">
        {SCRIPT.slice(0, visibleCount).map((l, i) => {
          const isCurrent = !reduced && i === pos.line;
          const typed = l.kind === 'cmd' && isCurrent ? l.text.slice(0, pos.char) : l.text;
          const showCursor = isCurrent;

          if (l.kind === 'cmd') {
            return (
              <div className="selfhost-term-line selfhost-term-line--cmd" key={i}>
                <span className="selfhost-term-prompt">$</span>
                <span className="selfhost-term-cmd">{typed}</span>
                {!isCurrent && l.comment && (
                  <span className="selfhost-term-comment">{`  ${l.comment}`}</span>
                )}
                {showCursor && <span className="selfhost-term-cursor" />}
              </div>
            );
          }
          return (
            <div className="selfhost-term-line selfhost-term-line--out" key={i}>
              {l.ok && <span className="selfhost-term-tick">✓</span>}
              <span className="selfhost-term-out">{typed}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SelfHostedSection() {
  return (
    <section className="selfhost-section reveal">
      <div className="selfhost-grid">
        {/* Left: the pitch */}
        <div className="selfhost-copy">
          <span className="selfhost-eyebrow">SELF-HOSTED</span>
          <h2 className="selfhost-title">Run the entire platform in your own cloud.</h2>
          <p className="selfhost-subtitle">
            The whole platform — control plane, triggers, and every marketplace agent —
            runs on a single Docker VM inside your network. Bring your own model key.
          </p>

          <div className="selfhost-cards">
            <div className="selfhost-card">
              <h3>Single-VM Docker</h3>
              <p>
                One <code>docker compose up</code> brings the whole platform online —
                every marketplace agent, plus MCP + cron + webhook + Slack triggers.
                No Kubernetes, no ops team.
              </p>
            </div>

            <div className="selfhost-card">
              <h3>Your data never leaves your network</h3>
              <p>
                Runs inside your VPC, air-gapped capable. Bring your own model key —
                Anthropic, OpenAI, Bedrock, or local. Free up to 10 agents.
              </p>
            </div>
          </div>

          <a className="selfhost-cta" href="mailto:support@zibby.dev?subject=Self-hosting%20Zibby">
            Talk to us about self-hosting →
          </a>
        </div>

        {/* Right: the animated terminal */}
        <div className="selfhost-stage">
          <AnimatedTerminal />
        </div>
      </div>
    </section>
  );
}
